import {
  getJurisdictionRegionBoundary,
  listJurisdictionRegions,
} from "@/hera-api/regions-api";
import {
  type DashboardMapOverlayPlan,
  type DrawableRegion,
  type MapClickEvent,
  REGION_LABEL_ORIGIN,
  createRegionClickController,
  orderRegionHitTargets,
  polygonGroupsFromGeometry,
  polygonOuterArea,
  regionLabelAnchor,
  regionsForHitTargets,
  regionsToDraw,
  resolveDashboardMapOverlay,
} from "@/lib/dashboard-map-region-overlay";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Same census-boundary treatment as the Regions map. */
const OUTSIDE_FILL = "#111827";
const OUTSIDE_OPACITY = 0.34;
const BOUNDARY_RED = "#e53935";
const BOUNDARY_DOT_SPACING = "6px";
/** Above the outside mask. Smaller regions get a higher index on top of this. */
const HIT_Z_BASE = 5;
/** Above every hit polygon so the name stays visible. */
const LABEL_Z = 1000;
/** Same type treatment as the region name on the Regions map. */
const REGION_LABEL = {
  color: "#374151",
  fontSize: "12px",
  fontWeight: "600",
  className: "rounded bg-white/90 px-1.5 py-0.5 shadow-sm",
} as const;

type LatLng = { lat: number; lng: number };
type Geometry = { type?: string; coordinates?: unknown } | null | undefined;

function signedArea(ring: LatLng[]): number {
  let sum = 0;
  for (let index = 0; index < ring.length; index++) {
    const start = ring[index];
    const end = ring[(index + 1) % ring.length];
    sum += (end.lng - start.lng) * (end.lat + start.lat);
  }
  return sum;
}

/** Holes have to wind opposite the clockwise cover or Maps fills the county. */
function asMaskHole(ring: LatLng[]): LatLng[] {
  return signedArea(ring) > 0 ? [...ring].reverse() : ring;
}

function clockwiseCover(
  south: number,
  west: number,
  north: number,
  east: number,
): LatLng[] {
  return [
    { lat: north, lng: west },
    { lat: north, lng: east },
    { lat: south, lng: east },
    { lat: south, lng: west },
  ];
}

function coverAround(map: google.maps.Map, rings: LatLng[][]): LatLng[] {
  const bounds = map.getBounds?.();
  if (bounds) {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latPad = Math.max((ne.lat() - sw.lat()) * 2, 1);
    const lngPad = Math.max((ne.lng() - sw.lng()) * 2, 1);
    return clockwiseCover(
      Math.max(sw.lat() - latPad, -85),
      sw.lng() - lngPad,
      Math.min(ne.lat() + latPad, 85),
      ne.lng() + lngPad,
    );
  }
  let south = 90;
  let north = -90;
  let west = 180;
  let east = -180;
  for (const ring of rings) {
    for (const point of ring) {
      south = Math.min(south, point.lat);
      north = Math.max(north, point.lat);
      west = Math.min(west, point.lng);
      east = Math.max(east, point.lng);
    }
  }
  return clockwiseCover(south - 8, west - 8, north + 8, east + 8);
}

function boundaryDot(): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: BOUNDARY_RED,
    fillOpacity: 1,
    strokeWeight: 0,
    scale: 1.6,
  };
}

type Overlay = { setMap: (map: google.maps.Map | null) => void };

/** Grey everything outside these rings. Inner rings of each polygon stay clear. */
function addOutsideMask(
  map: google.maps.Map,
  groups: LatLng[][][],
  overlays: Overlay[],
  cleanups: Array<() => void>,
) {
  const interior: LatLng[][] = [];
  const excluded: LatLng[][] = [];
  for (const rings of groups) {
    const [outer, ...holes] = rings;
    if (!outer) continue;
    interior.push(outer);
    for (const hole of holes) {
      if (hole.length >= 3) excluded.push(hole);
    }
  }
  if (interior.length === 0) return;

  const holes = interior.map(asMaskHole);
  const maskPaths = () => [coverAround(map, interior), ...holes];
  const mask = new google.maps.Polygon({
    paths: maskPaths(),
    map,
    clickable: false,
    strokeOpacity: 0,
    fillColor: OUTSIDE_FILL,
    fillOpacity: OUTSIDE_OPACITY,
    zIndex: 1,
  });
  overlays.push(mask);
  const idle = map.addListener("idle", () => {
    mask.setPaths(maskPaths());
  });
  cleanups.push(() => idle.remove());

  for (const hole of excluded) {
    overlays.push(
      new google.maps.Polygon({
        paths: hole,
        map,
        clickable: false,
        strokeOpacity: 0,
        fillColor: OUTSIDE_FILL,
        fillOpacity: OUTSIDE_OPACITY,
        zIndex: 1,
      }),
    );
  }
}

/** Grey the map outside these polygons. Returns a cleanup for the cover and its listener. */
export function attachOutsideMask(
  map: google.maps.Map,
  groups: LatLng[][][],
): () => void {
  const overlays: Overlay[] = [];
  const cleanups: Array<() => void> = [];
  addOutsideMask(map, groups, overlays, cleanups);
  return () => {
    for (const cleanup of cleanups) cleanup();
    for (const overlay of overlays) overlay.setMap(null);
  };
}

/**
 * Draw jurisdiction census boundaries the way the Regions map does.
 * One mask covers every selected jurisdiction so they do not darken each other.
 * Does not move the camera.
 */
function addJurisdictionBoundaries(
  map: google.maps.Map,
  geometries: readonly Geometry[],
  overlays: Overlay[],
  cleanups: Array<() => void>,
) {
  const groups = geometries.flatMap((geometry) =>
    polygonGroupsFromGeometry(geometry),
  );
  addOutsideMask(map, groups, overlays, cleanups);
  addDottedOutlines(map, groups, overlays);
}

/** Red dotted border on each polygon's outer ring. Same treatment as the Regions map. */
function addDottedOutlines(
  map: google.maps.Map,
  groups: LatLng[][][],
  overlays: Overlay[],
) {
  const icon = boundaryDot();
  for (const rings of groups) {
    const ring = rings[0];
    if (!ring) continue;
    overlays.push(
      new google.maps.Polyline({
        path: ring,
        map,
        clickable: false,
        strokeOpacity: 0,
        zIndex: 4,
        icons: [{ icon, offset: "0", repeat: BOUNDARY_DOT_SPACING }],
      }),
    );
  }
}

/** One name label per region, anchored to that polygon's south-east corner. */
function addRegionLabel(
  map: google.maps.Map,
  region: DrawableRegion,
  groups: LatLng[][][],
  overlays: Overlay[],
) {
  const text = region.name ?? "";
  const position = regionLabelAnchor(groups);
  if (!text.trim() || !position) return;
  try {
    overlays.push(
      new google.maps.Marker({
        map,
        position,
        clickable: false,
        zIndex: LABEL_Z,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 0,
          labelOrigin: new google.maps.Point(
            REGION_LABEL_ORIGIN.x,
            REGION_LABEL_ORIGIN.y,
          ),
        },
        label: {
          text,
          color: REGION_LABEL.color,
          fontSize: REGION_LABEL.fontSize,
          fontWeight: REGION_LABEL.fontWeight,
          className: REGION_LABEL.className,
        },
      }),
    );
  } catch {
    // A map build without Marker still draws the polygon.
  }
}

function isMarkerClickTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element && target.closest("gmp-advanced-marker") !== null
  );
}

export type RegionHover = { name: string; x: number; y: number };

function setRegionCursor(map: google.maps.Map, active: boolean) {
  const div = map.getDiv?.();
  if (div) div.style.cursor = active ? "pointer" : "";
  map.setOptions?.({ draggableCursor: active ? "pointer" : undefined });
}

function listen(
  polygon: google.maps.Polygon,
  name: string,
  fn: (event: MapClickEvent) => void,
  cleanups: Array<() => void>,
) {
  const listener = polygon.addListener(name, fn);
  cleanups.push(() => listener.remove());
}

/**
 * Invisible clickable cover for one region. Fill and stroke stay at zero so
 * the mask and dotted borders drawn underneath are unchanged.
 */
function addRegionHitTarget(
  map: google.maps.Map,
  region: DrawableRegion,
  zIndex: number,
  overlays: Overlay[],
  cleanups: Array<() => void>,
  onClick: (event: MapClickEvent) => void,
  onHover: (hover: RegionHover | null) => void,
) {
  const name = region.name?.trim() ?? "";
  const show = (event: MapClickEvent) => {
    if (isMarkerClickTarget(event.domEvent?.target ?? null)) {
      setRegionCursor(map, false);
      onHover(null);
      return;
    }
    setRegionCursor(map, true);
    const x = event.domEvent?.clientX;
    const y = event.domEvent?.clientY;
    if (!name || typeof x !== "number" || typeof y !== "number") {
      onHover(null);
      return;
    }
    onHover({ name, x, y });
  };
  const groups = polygonGroupsFromGeometry(region.geometry);
  for (const paths of groups) {
    try {
      const polygon = new google.maps.Polygon({
        paths,
        map,
        clickable: true,
        strokeOpacity: 0,
        strokeWeight: 0,
        fillOpacity: 0,
        zIndex,
      });
      overlays.push(polygon);
      listen(polygon, "mouseover", show, cleanups);
      listen(polygon, "mousemove", show, cleanups);
      listen(
        polygon,
        "mouseout",
        () => {
          setRegionCursor(map, false);
          onHover(null);
        },
        cleanups,
      );
      listen(polygon, "click", onClick, cleanups);
    } catch {
      // Skip a ring the Maps API rejects. Other regions still draw.
    }
  }
}

/**
 * Install region or jurisdiction overlays. Callers must not fit the map from
 * here: overlay refreshes keep the current viewport.
 *
 * Overlapping regions: the smallest outer-ring area is topmost and receives
 * hover and click. Equal areas break the tie toward the lexicographically
 * greater region id.
 */
export function mountDashboardRegionOverlays(
  map: google.maps.Map,
  input: {
    plan: DashboardMapOverlayPlan;
    regions: readonly DrawableRegion[];
    boundaries: readonly Geometry[];
    selectedRegionIds: readonly string[];
    onRegionFilterChange: (regionIds: string[]) => void;
    onRegionHover: (hover: RegionHover | null) => void;
  },
): () => void {
  const overlays: Overlay[] = [];
  const cleanups: Array<() => void> = [];
  const controller = createRegionClickController({
    clicksEnabled: input.plan.clicksEnabled,
    selectedRegionIds: input.selectedRegionIds,
    onRegionFilterChange: input.onRegionFilterChange,
  });

  if (input.plan.clicksEnabled) {
    const mapClick = map.addListener("click", () => controller.onMapClick());
    cleanups.push(() => mapClick.remove());
    const div = map.getDiv?.();
    if (div) {
      const onMarkerClick = (event: Event) => {
        if (!isMarkerClickTarget(event.target)) return;
        controller.suppressBackgroundClick();
      };
      const onMarkerHover = (event: Event) => {
        if (!isMarkerClickTarget(event.target)) return;
        setRegionCursor(map, false);
        input.onRegionHover(null);
      };
      div.addEventListener("click", onMarkerClick, true);
      div.addEventListener("mouseover", onMarkerHover, true);
      div.addEventListener("mousemove", onMarkerHover, true);
      cleanups.push(() => {
        div.removeEventListener("click", onMarkerClick, true);
        div.removeEventListener("mouseover", onMarkerHover, true);
        div.removeEventListener("mousemove", onMarkerHover, true);
      });
    }
  }

  const hitTargets = regionsForHitTargets(input.regions, {
    selectedRegionIds: input.selectedRegionIds,
    narrowToSelectedJurisdictions: input.plan.narrowHitsToSelectedJurisdictions,
  });

  if (input.plan.mode === "jurisdiction-boundaries") {
    addJurisdictionBoundaries(map, input.boundaries, overlays, cleanups);
  } else if (input.plan.mode === "selected-regions") {
    const drawable = regionsToDraw(hitTargets, input.plan.regionIdsToDraw);
    addOutsideMask(
      map,
      drawable.flatMap((region) => polygonGroupsFromGeometry(region.geometry)),
      overlays,
      cleanups,
    );
    for (const region of drawable) {
      const groups = polygonGroupsFromGeometry(region.geometry);
      addDottedOutlines(map, groups, overlays);
      addRegionLabel(map, region, groups, overlays);
    }
  }

  if (input.plan.clicksEnabled) {
    const ranked = orderRegionHitTargets(
      hitTargets.map((region) => ({
        region,
        id: region.id,
        area: polygonOuterArea(polygonGroupsFromGeometry(region.geometry)),
      })),
    );
    ranked.forEach((entry, index) => {
      addRegionHitTarget(
        map,
        entry.region,
        HIT_Z_BASE + index,
        overlays,
        cleanups,
        (event) => {
          if (isMarkerClickTarget(event.domEvent?.target ?? null)) {
            controller.suppressBackgroundClick();
            return;
          }
          controller.onRegionClick(event, entry.region.id);
        },
        input.onRegionHover,
      );
    });
  }

  return () => {
    setRegionCursor(map, false);
    input.onRegionHover(null);
    for (const cleanup of cleanups) cleanup();
    for (const overlay of overlays) overlay.setMap(null);
  };
}

function useStableIds(ids: readonly string[]): readonly string[] {
  const key = ids.join("\0");
  const stored = useRef({ key, ids });
  if (stored.current.key !== key) stored.current = { key, ids };
  return stored.current.ids;
}

export function DashboardMapRegionOverlay({
  selectedRegionIds,
  selectedJurisdictionIds,
  portalJurisdictionIds,
  onRegionFilterChange,
}: {
  selectedRegionIds: readonly string[];
  selectedJurisdictionIds: readonly string[];
  portalJurisdictionIds: readonly string[];
  onRegionFilterChange: (regionIds: string[]) => void;
}) {
  const map = useMap();
  const [hover, setHover] = useState<RegionHover | null>(null);
  const onChangeRef = useRef(onRegionFilterChange);
  onChangeRef.current = onRegionFilterChange;
  const onHoverRef = useRef(setHover);
  onHoverRef.current = setHover;
  const stableSelectedRegionIds = useStableIds(selectedRegionIds);
  const stableSelectedJurisdictionIds = useStableIds(selectedJurisdictionIds);
  const stablePortalJurisdictionIds = useStableIds(portalJurisdictionIds);
  const plan = useMemo(
    () =>
      resolveDashboardMapOverlay({
        selectedRegionIds: stableSelectedRegionIds,
        selectedJurisdictionIds: stableSelectedJurisdictionIds,
        portalJurisdictionIds: stablePortalJurisdictionIds,
      }),
    [
      stablePortalJurisdictionIds,
      stableSelectedJurisdictionIds,
      stableSelectedRegionIds,
    ],
  );
  const regionQuery = useQuery({
    queryKey: ["jurisdiction-regions", plan.regionJurisdictionIds],
    queryFn: () => listJurisdictionRegions(plan.regionJurisdictionIds),
    enabled: plan.regionJurisdictionIds.length > 0,
  });
  const boundaryQueryDefs = useMemo(
    () =>
      plan.boundaryJurisdictionIds.map((jurisdictionId) => ({
        queryKey: ["jurisdiction-region-boundary", jurisdictionId],
        queryFn: () => getJurisdictionRegionBoundary(jurisdictionId),
      })),
    [plan],
  );
  const boundaryQueries = useQueries({ queries: boundaryQueryDefs });
  const boundaryStamp = boundaryQueries
    .map((query) => `${query.dataUpdatedAt}:${query.status}`)
    .join("|");
  const boundariesRef = useRef<Geometry[]>([]);
  boundariesRef.current = boundaryQueries.flatMap((query) =>
    query.data?.geometry ? [query.data.geometry] : [],
  );
  const regionsRef = useRef<DrawableRegion[]>([]);
  regionsRef.current = regionQuery.data?.regions ?? [];

  // regionQuery.data and boundaryStamp are the signals that the refs above changed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: overlay refresh follows query results, not ref identity
  useEffect(() => {
    if (!map) return;
    return mountDashboardRegionOverlays(map, {
      plan,
      regions: regionsRef.current,
      boundaries: boundariesRef.current,
      selectedRegionIds: stableSelectedRegionIds,
      onRegionFilterChange: (regionIds) => onChangeRef.current(regionIds),
      onRegionHover: (next) => onHoverRef.current(next),
    });
  }, [map, plan, regionQuery.data, boundaryStamp, stableSelectedRegionIds]);

  if (!hover) return null;
  return createPortal(
    <div
      role="tooltip"
      className="pointer-events-none fixed z-[70] rounded bg-white/95 px-1.5 py-0.5 text-xs font-semibold text-gray-700 shadow-sm"
      style={{ left: hover.x + 12, top: hover.y + 12 }}
    >
      {hover.name}
    </div>,
    document.body,
  );
}
